// Client for the profile video endpoints (cookie session, same origin).

// Server limit (nginx rejects bodies over 64 MB)
export const MAX_VIDEO_BYTES = 60 * 1024 * 1024;
export const WARN_VIDEO_BYTES = 50 * 1024 * 1024;
export const STOP_VIDEO_BYTES = 58 * 1024 * 1024;

export function formatDuration(sec) {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

export function formatSize(bytes) {
  const b = Number(bytes) || 0;
  if (b >= 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} МБ`;
  return `${Math.max(1, Math.round(b / 1024))} КБ`;
}

export function formatDate(value, language) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(language === 'en' ? 'en-GB' : 'ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const RECORDER_TYPES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
  'video/mp4;codecs=avc1,mp4a.40.2',
  'video/mp4',
];

export function pickRecorderMimeType() {
  if (typeof MediaRecorder === 'undefined') return '';
  if (typeof MediaRecorder.isTypeSupported !== 'function') return '';
  return RECORDER_TYPES.find((t) => MediaRecorder.isTypeSupported(t)) || '';
}

export function canRecordInBrowser() {
  return (
    typeof window !== 'undefined' &&
    typeof MediaRecorder !== 'undefined' &&
    !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
  );
}

const EXT_BY_TYPE = {
  'video/webm': 'webm',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/x-matroska': 'mkv',
};

function baseType(blob, filename) {
  const raw = (blob.type || '').split(';')[0].trim().toLowerCase();
  if (raw) return raw;
  const ext = (filename || '').split('.').pop().toLowerCase();
  return (
    Object.keys(EXT_BY_TYPE).find((k) => EXT_BY_TYPE[k] === ext) || 'video/webm'
  );
}

export function clipFilename(blob, name) {
  if (name && /\.[a-z0-9]{2,4}$/i.test(name)) return name;
  const ext = EXT_BY_TYPE[baseType(blob, name)] || 'webm';
  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  return `assessment-${stamp}.${ext}`;
}

const base = (profileId) =>
  `/api/profiles/${encodeURIComponent(profileId)}/videos`;

async function readError(res) {
  const data = await res.json().catch(() => ({}));
  return data.message || `HTTP ${res.status}`;
}

export async function listVideos(profileId) {
  const res = await fetch(base(profileId), { credentials: 'include' });
  if (!res.ok) throw new Error(await readError(res));
  const data = await res.json().catch(() => []);
  return Array.isArray(data) ? data : [];
}

// Every clip of a student across all assessments (with profile labels)
export async function listStudentVideos(studentId) {
  const res = await fetch(
    `/api/profiles/student/${encodeURIComponent(studentId)}/videos`,
    { credentials: 'include' }
  );
  if (!res.ok) throw new Error(await readError(res));
  const data = await res.json().catch(() => []);
  return Array.isArray(data) ? data : [];
}

export async function deleteVideo(profileId, assetId) {
  const res = await fetch(`${base(profileId)}/${encodeURIComponent(assetId)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) throw new Error(await readError(res));
}

// Multipart upload with progress. Resolves with the created asset.
export function uploadVideo(
  profileId,
  blob,
  { title, durationSec, filename, onProgress } = {}
) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', base(profileId));
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      let data = {};
      try {
        data = JSON.parse(xhr.responseText);
      } catch (e) {
        /* non-JSON body */
      }
      if (xhr.status >= 200 && xhr.status < 300 && data.asset) {
        resolve(data.asset);
      } else if (xhr.status === 413) {
        reject(new Error(data.message || 'Файл слишком большой'));
      } else {
        reject(new Error(data.message || `Ошибка загрузки (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('Ошибка сети'));
    xhr.ontimeout = () => reject(new Error('Ошибка сети'));
    xhr.onabort = () => reject(new Error('Загрузка прервана'));

    // Codec parameters in the part's Content-Type can trip server type checks
    const type = baseType(blob, filename);
    const part = new Blob([blob], { type });
    const fd = new FormData();
    if (durationSec) fd.append('durationSec', String(Math.round(durationSec)));
    if (title) fd.append('title', title);
    fd.append('video', part, clipFilename(part, filename));
    xhr.send(fd);
  });
}
